"use client";
import { sectionsService } from "@/services/sectionsService";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";

function stripEmptyClassFromHtml(html) {
  if (!html || typeof html !== "string") return html;
  return html.replace(/\sclass\s*=\s*["']["']/gi, "");
}

// Adapter function to normalize different FAQ data structures
function normalizeFaqData(rawData, dataType = "default") {
  if (!rawData) return [];

  switch (dataType) {
    case "cityDynamic":
      // CityLandingPage API structure
      const dynamicQuestions =
        rawData?.CityDynamicQuestion?.DynamicQuestions || [];
      return dynamicQuestions
        .filter((item) => item.ShowContent) // Only show items with ShowContent: true
        .map((item, index) => ({
          Key: `city-${index}`,
          Title: item.Question,
          Description: item.Answer,
          Url: null,
          ParentUrl: null,
        }));

    case "flightRouteDynamic":
      // FlightRouteLandingPage API structure
      const flightRouteQuestions =
        rawData?.FlightRouteDynamicQuestion?.DynamicQuestions || [];
      return flightRouteQuestions
        .filter((item) => item.ShowContent) // Only show items with ShowContent: true
        .map((item, index) => ({
          Key: `flightRoute-${index}`,
          Title: item.Question,
          Description: item.Answer,
          Url: null,
          ParentUrl: null,
        }));

    case "airlineDynamic":
      // AirlineLandingPage API structure
      const airlineQuestions =
        rawData?.aDynamicQuestionWrapper?.DynamicQuestions || [];
      const filteredQuestions = airlineQuestions.filter(
        (item) => item.ShowContent,
      );

      const result = filteredQuestions.map((item, index) => ({
        Key: `airline-${index}`,
        Title: item.Question,
        Description: item.Answer,
        Url: null,
        ParentUrl: null,
      }));
      return result;

    case "countryDynamic":
      // CountryLandingPage API structure
      const countryQuestions =
        rawData?.CountryDynamicQuestion?.DynamicQuestions || [];
      return countryQuestions
        .filter((item) => item.ShowContent) // Only show items with ShowContent: true
        .map((item, index) => ({
          Key: `country-${index}`,
          Title: item.Question,
          Description: item.Answer,
          Url: null,
          ParentUrl: null,
        }));

    case "flightTicketPage": {
      const categories = Array.isArray(rawData) ? rawData : [];
      const out = [];
      categories.forEach((cat, catIdx) => {
        (cat.Questions || []).forEach((q, qIdx) => {
          out.push({
            Key: `ftp-${catIdx}-${qIdx}`,
            Title: q.Question,
            Description: q.Answer,
            Url: null,
            ParentUrl: null,
          });
        });
      });
      return out;
    }

    case "default":
    default:
      // Default structure (existing FAQ API)
      return rawData?.CategoryQuestions || rawData || [];
  }
}

export default function FaqAccordion({
  customFaqData = null,
  dataType = "default", // 'cityDynamic', 'categoryQuestions', 'default'
  customTitle = null,
}) {
  const [openItems, setOpenItems] = useState({});
  const locale = useLocale();

  const normalizedFromProps = useMemo(() => {
    if (!customFaqData) return null;
    return normalizeFaqData(customFaqData, dataType);
  }, [customFaqData, dataType]);

  const [fetchedFaqData, setFetchedFaqData] = useState([]);

  useEffect(() => {
    if (normalizedFromProps) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await sectionsService.getFaq({
          LanguageCode: locale.toUpperCase(),
        });
        const normalizedData = normalizeFaqData(response, "default");
        if (!cancelled) setFetchedFaqData(normalizedData);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedFromProps, locale]);

  const faqData = normalizedFromProps ?? fetchedFaqData;

  const isItemOpen = (index) => openItems[index] === true;

  const toggle = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Use custom title if provided, otherwise use default
  const sectionTitle = customTitle || "Sıkça Sorulan Sorular";

  return (
    <div className="flex flex-col gap-4 md:gap-8">
      <SectionHeader title={sectionTitle} />
      <div>
        {Array.isArray(faqData) &&
          faqData.map((item, index) => (
            <div
              key={item.Key}
              className={clsx(
                "flex w-full items-start justify-between gap-6 py-4 md:py-6",
                index !== faqData.length - 1 && "border-border-weak border-b",
              )}
            >
              <div className={clsx("flex w-full flex-col")}>
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="flex w-full cursor-pointer"
                  aria-expanded={isItemOpen(index)}
                  aria-controls={`faq-${index}`}
                >
                  <span className="color-foreground  text-left text-lg leading-6 font-semibold">
                    {item.Title}
                  </span>
                </button>

                <div
                  className={clsx(
                    "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out",
                    isItemOpen(index) ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <div
                      id={`faq-${index}`}
                      className="color-foreground  pt-4 text-left text-base leading-6 font-normal"
                      aria-hidden={!isItemOpen(index)}
                      dangerouslySetInnerHTML={{
                        __html: stripEmptyClassFromHtml(item.Description),
                      }}
                    />
                  </div>
                </div>
              </div>
              <FontAwesomeIcon
                icon={byPrefixAndName.far["chevron-down"]}
                className={clsx("text-xl transition-transform", {
                  "rotate-180": isItemOpen(index),
                })}
              />
            </div>
          ))}
      </div>
    </div>
  );
}
